import { useEffect, useMemo, useState } from 'react'
import type { EventData, Mark } from '../types'
import { getEvent } from '../api'
import { getVisited, getMyParticipants } from '../lib/identity'
import MyGrid, { type MyGridEvent, type MyGridRow } from '../components/MyGrid'

const RANK: Record<Mark, number> = { ok: 3, maybe: 2, no: 1 }

interface MyMark {
  eventId: string
  eventTitle: string
  date: string
  mark: Mark
}

export default function MyPage() {
  const visited = useMemo(() => getVisited(), [])
  const myMap = useMemo(() => getMyParticipants(), [])
  const [events, setEvents] = useState<EventData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (visited.length === 0) {
      setLoading(false)
      return
    }
    Promise.allSettled(visited.map((v) => getEvent(v.id))).then((results) => {
      setEvents(
        results
          .filter((r): r is PromiseFulfilledResult<EventData> => r.status === 'fulfilled')
          .map((r) => r.value)
      )
      setLoading(false)
    })
  }, [visited])

  const { cols, rows, answeredCount, undated } = useMemo(() => {
    const marks: MyMark[] = []
    let undated = 0
    let answeredCount = 0
    for (const ev of events) {
      const myPid = myMap[ev.event.id]
      if (!myPid) continue
      const mine = ev.responses.filter((r) => r.participant_id === myPid)
      if (mine.length > 0) answeredCount++
      const candById = new Map(ev.candidates.map((c) => [c.id, c]))
      for (const r of mine) {
        const c = candById.get(r.candidate_id)
        if (!c) continue
        if (!c.date) {
          if (r.mark !== 'no') undated++
          continue
        }
        marks.push({ eventId: ev.event.id, eventTitle: ev.event.title, date: c.date, mark: r.mark })
      }
    }

    // ○/△ がある日だけを行に（自分の「予定がある日」）
    const rowDates = Array.from(
      new Set(marks.filter((m) => m.mark !== 'no').map((m) => m.date))
    ).sort((a, b) => a.localeCompare(b))
    const rowDateSet = new Set(rowDates)

    const cellMap = new Map<string, Mark>()
    const titleById = new Map<string, string>()
    for (const m of marks) {
      titleById.set(m.eventId, m.eventTitle)
      const k = `${m.date}|${m.eventId}`
      const cur = cellMap.get(k)
      if (!cur || RANK[m.mark] > RANK[cur]) cellMap.set(k, m.mark)
    }

    const colOrder: string[] = []
    const seen = new Set<string>()
    for (const m of marks) {
      if (!rowDateSet.has(m.date) || seen.has(m.eventId)) continue
      seen.add(m.eventId)
      colOrder.push(m.eventId)
    }
    const cols: MyGridEvent[] = colOrder.map((id) => ({ id, title: titleById.get(id) ?? id }))

    const rows: MyGridRow[] = rowDates.map((date) => ({
      date,
      cells: Object.fromEntries(cols.map((e) => [e.id, cellMap.get(`${date}|${e.id}`)])),
    }))

    return { cols, rows, answeredCount, undated }
  }, [events, myMap])

  return (
    <div className="animate-in mx-auto max-w-3xl px-4 pb-16 pt-5">
      <div className="mb-3 text-sm">
        <a href="/" className="font-medium text-muted transition hover:text-brand">
          ← ヨテピース
        </a>
      </div>
      <h1 className="font-display text-2xl font-bold tracking-tight text-ink">マイ予定</h1>
      <p className="mb-6 mt-1 text-sm text-muted">
        この端末で開いたイベントでの、あなたの回答をまとめて表示します。
      </p>

      {loading && <p className="text-center text-muted">読み込み中…</p>}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-line bg-surface p-8 text-center text-muted">
          まだ日付つきの回答がありません。
          <br />
          イベントで出欠を入力すると、ここにまとめて表示されます。
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <p className="mb-3 text-sm font-medium text-muted">
            ○・△をつけた予定 <span className="text-ink">{rows.length}</span>日 ／ 回答済み{' '}
            <span className="text-ink">{answeredCount}</span>件
          </p>
          <MyGrid events={cols} rows={rows} />
          {undated > 0 && (
            <p className="mt-4 text-xs text-muted">
              ※日付の読めない候補が {undated}件あり、一覧には含めていません。
            </p>
          )}
        </>
      )}
    </div>
  )
}
